import { cn } from "@newxsystems/shared";
import type { CSSProperties } from "react";
import { resolveKaajPrimitive } from "../../../../client";
import { getKaajSectionSpacing } from "../../../../modules/layout/kaaj-section-spacing";
import { RichTextHtml } from "../../RichTextHtml";
import { KaajAction } from "../_shared/KaajAction";
import type { IImageGridBlockProps } from "./ImageGridBlock.props";

type ImageGridTheme = {
  card: string;
  chip: string;
  credit: string;
  frame: string;
  media: string;
  rule: string;
  section: string;
  subheading: string;
  tag: string;
};

const lightTheme: ImageGridTheme = {
  card: "border-[#D9E1FF] bg-white/98 shadow-[0_20px_58px_rgba(33,68,255,0.08)] hover:border-[#B8C7FF] hover:shadow-[0_28px_72px_rgba(33,68,255,0.13)]",
  chip: "border-[#CAD7FF] bg-[#EEF2FF] text-[#2144FF]",
  credit: "text-[#667085]",
  frame: "border-[#D9E1FF] bg-[#FAFBFF]",
  media: "bg-[#EEF2FF]",
  rule: "border-[#E6ECFF]",
  section: "text-[#151927]",
  subheading: "text-[#4B5568]",
  tag: "border-[#D9E1FF] bg-white text-[#405076]",
};

const alignmentClasses = {
  left: { text: "items-start text-left", prose: "" },
  center: { text: "mx-auto items-center text-center", prose: "mx-auto" },
};

const getObjectPosition = (
  item: NonNullable<IImageGridBlockProps["items"]>[number],
): CSSProperties | undefined => {
  const focalPoint = item.image.focalPoint;

  return focalPoint ? { objectPosition: `${focalPoint.x}% ${focalPoint.y}%` } : undefined;
};

export const ImageGridBlock = (props: IImageGridBlockProps) => {
  const { parentClass } = props;
  const Image = resolveKaajPrimitive("Image");
  const sectionId = props.anchorId ?? undefined;
  const headingId = props.heading && sectionId ? `${sectionId}-heading` : undefined;
  const styles = lightTheme;
  const alignment = alignmentClasses.left;
  const items = props.items ?? [];
  const usesTwoColumns = items.length === 2;
  const usesFourColumns = items.length > 0 && items.length % 4 === 0;

  if (
    !props.eyebrow &&
    !props.heading &&
    !props.subheading &&
    !props.buttons?.length &&
    items.length === 0
  ) {
    return null;
  }

  return (
    <section
      aria-labelledby={headingId}
      className={cn(
        "relative isolate overflow-hidden",
        styles.section,
        getKaajSectionSpacing(props.spacing),
        parentClass,
      )}
      data-kaaj-block="imageGrid"
      id={sectionId ?? undefined}
    >
      <div className="kaaj-container relative flex flex-col gap-10 lg:gap-12">
        {(props.eyebrow || props.heading || props.subheading) && (
          <div className={cn("flex flex-col gap-4", alignment.text)}>
            {props.eyebrow ? (
              <p
                className={cn(
                  "inline-flex max-w-full rounded-full border px-3 py-1.5 text-[0.72rem] font-extrabold uppercase tracking-[0.16em] [overflow-wrap:anywhere]",
                  styles.chip,
                )}
              >
                {props.eyebrow}
              </p>
            ) : null}
            {props.heading ? (
              <h2 className="kaaj-section-heading text-current" id={headingId}>
                {props.heading}
              </h2>
            ) : null}
            {props.subheading ? (
              <RichTextHtml
                className={cn(
                  "text-pretty text-base leading-7 md:text-lg md:leading-8",
                  styles.subheading,
                  alignment.prose,
                )}
                html={props.subheading}
              />
            ) : null}
          </div>
        )}

        {items.length > 0 ? (
          <div
            className={cn(
              "grid grid-cols-1 gap-5 sm:grid-cols-2 lg:gap-6",
              usesTwoColumns
                ? "lg:grid-cols-2"
                : usesFourColumns
                  ? "lg:grid-cols-4"
                  : "lg:grid-cols-3",
            )}
          >
            {items.map((item, index) => {
              const imageWidth = item.image.width ?? item.width ?? undefined;
              const imageHeight = item.image.height ?? item.height ?? undefined;
              const category = item.category;
              const caption = item.caption;
              const credit = item.credit;
              const tags = item.tags ?? [];
              const mediaStyle: CSSProperties = {
                aspectRatio: item.aspectRatio ?? "4 / 3",
              };
              const hasCaptionContent = Boolean(caption || credit || category || tags.length > 0);

              return (
                <article
                  className={cn(
                    "group relative isolate flex h-full min-w-0 flex-col overflow-hidden rounded-[1.8rem] border p-2.5 transition-[border-color,box-shadow,transform] duration-300 motion-reduce:transition-none motion-safe:hover:-translate-y-1 sm:p-3",
                    styles.card,
                  )}
                  key={`${item.image.defaultSrc}-${index}`}
                >
                  <figure className="flex h-full min-w-0 flex-col">
                    <div
                      className={cn(
                        "relative w-full overflow-hidden rounded-[1.35rem] border",
                        styles.frame,
                      )}
                      style={mediaStyle}
                    >
                      <div aria-hidden="true" className={cn("absolute inset-0", styles.media)} />
                      <Image
                        alt={item.image.alternateText}
                        className="relative z-10 h-full w-full object-cover transition duration-500 motion-reduce:transition-none motion-safe:group-hover:scale-[1.025]"
                        decoding="async"
                        height={imageHeight ?? undefined}
                        loading={index < 3 ? "eager" : "lazy"}
                        priority={index < 3}
                        sizes={
                          item.image.sizes ??
                          (usesTwoColumns
                            ? "(min-width: 1024px) 46vw, (min-width: 640px) 45vw, 92vw"
                            : usesFourColumns
                              ? "(min-width: 1024px) 23vw, (min-width: 640px) 45vw, 92vw"
                              : "(min-width: 1024px) 31vw, (min-width: 640px) 45vw, 92vw")
                        }
                        src={item.image.defaultSrc}
                        srcSet={item.image.srcSet ?? undefined}
                        style={getObjectPosition(item)}
                        width={imageWidth ?? undefined}
                      />
                      <div className="pointer-events-none absolute inset-0 ring-1 ring-inset ring-white/28" />
                    </div>

                    {hasCaptionContent ? (
                      <figcaption className="flex flex-1 flex-col gap-3 px-2 pb-2 pt-4 text-sm sm:px-3">
                        {(category || tags.length > 0) && (
                          <div className="flex flex-wrap items-center gap-2">
                            {category ? (
                              <span
                                className={cn(
                                  "inline-flex max-w-full rounded-full border px-3 py-1 text-[0.65rem] font-extrabold uppercase leading-none tracking-[0.16em] [overflow-wrap:anywhere]",
                                  styles.chip,
                                )}
                              >
                                {category}
                              </span>
                            ) : null}
                            {tags.length > 0 ? (
                              <ul
                                aria-label="Image tags"
                                className="flex list-none flex-wrap gap-1.5 p-0"
                              >
                                {tags.map((tag, tagIndex) => (
                                  <li
                                    className={cn(
                                      "rounded-full border px-2.5 py-1 text-[0.72rem] font-semibold leading-none [overflow-wrap:anywhere]",
                                      styles.tag,
                                    )}
                                    key={`${item.image.defaultSrc}-${tag.tag}-${tagIndex}`}
                                  >
                                    {tag.tag}
                                  </li>
                                ))}
                              </ul>
                            ) : null}
                          </div>
                        )}

                        {(caption || credit) && (
                          <div className={cn("space-y-2 border-t pt-3", styles.rule)}>
                            {caption ? (
                              <p className="text-pretty text-base font-extrabold leading-6 text-current [overflow-wrap:anywhere]">
                                {caption}
                              </p>
                            ) : null}
                            {credit ? (
                              <p className={cn("text-xs font-medium leading-5", styles.credit)}>
                                {credit}
                              </p>
                            ) : null}
                          </div>
                        )}
                      </figcaption>
                    ) : null}
                  </figure>
                </article>
              );
            })}
          </div>
        ) : null}

        {props.buttons?.length ? (
          <div className="flex flex-wrap gap-3">
            {props.buttons.map((button, index) => (
              <KaajAction
                ariaLabel={button.ariaLabel}
                href={button.href}
                key={`${button.href}-${button.text}-${index}`}
                label={button.text}
                target={button.target}
                variant={button.style}
              />
            ))}
          </div>
        ) : null}
      </div>
    </section>
  );
};

ImageGridBlock.displayName = "Block::ImageGridBlock";
